import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Text } from '../components/Texte';
import { Button } from '../components/Button';
import { GradientButton } from '../components/GradientButton';
import { GenerationProgress } from '../components/GenerationProgress';
import { PiedAction } from '../components/PiedAction';
import { ScreenHeader } from '../components/ScreenHeader';
import { CarteModificationLettre } from '../components/CarteModificationLettre';
import { useClaude } from '../hooks/useClaude';
import { saveCoverLetter } from '../services/coverLetter';
import type { LetterChange, LetterImproveDiagnostic, LetterProfil, SavedCoverLetter } from '../types';
import {
  appliquerModificationsLettre,
  lettreAmeliorable,
  modificationApplicableLettre,
  nombreAppliqueesLettre,
} from '../utils/ameliorationLettre';
import { compterMotsLettre } from '../utils/letterText';
import { t, tBrut } from '../i18n';
import { colors, screenPadding, spacing, typography } from '../theme/tokens';

export interface CoverLetterImproveScreenProps {
  /** Lettre à améliorer : elle reste intacte, l'amélioration est enregistrée comme une NOUVELLE lettre. */
  letter: SavedCoverLetter;
  /** Ce que l'analyse a relevé, pour orienter les modifications. Absent : amélioration sans diagnostic. */
  diagnostic?: LetterImproveDiagnostic;
  annonce?: string;
  /** CV principal sérialisé ; vide si la personne n'en a pas. */
  cv?: string;
  profil: LetterProfil;
  onBack?: () => void;
  // Signale à la couche navigation qu'un résultat non appliqué serait perdu (garde de sortie).
  onDirtyChange?: (dirty: boolean) => void;
  /** Appelé avec la nouvelle lettre, déjà enregistrée dans « Mes lettres ». */
  onDone?: (letter: SavedCoverLetter) => void;
}

/**
 * « Améliorer ma lettre » (1.2.0) : un seul appel IA rend des modifications précises, paragraphe par
 * paragraphe, que la personne garde ou écarte une à une avant de les appliquer.
 * loading → revue → applying → done ; vide et blocked à part. `done` reste affiché si l'on revient de la
 * lettre créée : un retour ne doit pas retomber sur un écran d'attente. `vide` : aucune modification utile, et rien
 * n'est décompté. L'appel part dès l'ouverture : c'est le bouton de l'analyse qui a valu confirmation.
 */
type Phase = 'loading' | 'revue' | 'applying' | 'done' | 'vide' | 'blocked';

export function CoverLetterImproveScreen({
  letter,
  diagnostic,
  annonce,
  cv,
  profil,
  onBack,
  onDirtyChange,
  onDone,
}: CoverLetterImproveScreenProps) {
  const { improveCoverLetter, error, quotaFeature } = useClaude();
  const [phase, setPhase] = useState<Phase>('loading');
  const [changes, setChanges] = useState<LetterChange[]>([]);
  // Modifications gardées : toutes au départ, la personne écarte ce qui ne lui convient pas.
  const [retenues, setRetenues] = useState<ReadonlySet<string>>(new Set());
  const [localError, setLocalError] = useState<string | null>(null);
  const [nouvelle, setNouvelle] = useState<SavedCoverLetter | null>(null);
  // Parcours terminé : sans ce drapeau, un retour ultérieur sur cet écran déclencherait une alerte
  // pour un flux pourtant abouti.
  const [finished, setFinished] = useState(false);
  const monte = useRef(true);
  // Garde SYNCHRONE contre le double appui : `disabled` ne prend effet qu'au rendu suivant, et deux
  // appuis rapprochés enregistreraient deux lettres.
  const enregistrement = useRef(false);
  // Un seul appel au montage, même si l'effet est rejoué.
  const lance = useRef(false);
  useEffect(() => {
    monte.current = true;
    return () => {
      monte.current = false;
    };
  }, []);

  const lancer = useCallback(async () => {
    setLocalError(null);
    setPhase('loading');
    const { objet, paragraphes } = lettreAmeliorable(letter.content);
    const result = await improveCoverLetter({
      objet,
      paragraphes,
      annonce: annonce ?? '',
      cv: cv ?? '',
      profil,
      langue: letter.output_language,
      ...(diagnostic ? { diagnostic } : {}),
    });
    if (!monte.current) return;
    if (!result) {
      // null = quota (Paywall déjà ouvert par useClaude) ou erreur réseau.
      setPhase('blocked');
      return;
    }
    if (!Array.isArray(result.changes)) {
      // Réponse incomplète (serveur pas à jour, coupure) : un message, jamais un écran figé.
      setLocalError(t('coverLetterImprove.reponseInvalide'));
      setPhase('blocked');
      return;
    }
    // Une proposition qui ne vise plus le texte de la lettre ne s'afficherait que pour échouer.
    const applicables = result.changes.filter((m) => modificationApplicableLettre(letter.content, m));
    if (applicables.length === 0) {
      setPhase('vide');
      return;
    }
    setChanges(applicables);
    setRetenues(new Set(applicables.map((m) => m.id)));
    setPhase('revue');
  }, [annonce, cv, diagnostic, improveCoverLetter, letter, profil]);

  useEffect(() => {
    if (lance.current) return;
    lance.current = true;
    void lancer();
  }, [lancer]);

  // Les propositions ont déjà consommé un cover_letter_improve : les quitter sans les appliquer les perd.
  const dirty = !finished && (phase === 'revue' || phase === 'applying');
  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  const total = changes.length;
  const appliquees = useMemo(
    () => nombreAppliqueesLettre(letter.content, changes, retenues),
    [letter, changes, retenues],
  );
  // Longueur du corps avant et après : le serveur borne chaque modification, mais la personne voit
  // l'effet cumulé de ce qu'elle retient sur la page.
  const motsAvant = useMemo(() => compterMotsLettre(letter.content.lettre.paragraphes), [letter]);
  const motsApres = useMemo(
    () => appliquerModificationsLettre(letter.content, changes, retenues).nombre_mots,
    [letter, changes, retenues],
  );

  const basculer = (id: string, retenue: boolean) =>
    setRetenues((prec) => {
      const suivant = new Set(prec);
      if (retenue) suivant.add(id);
      else suivant.delete(id);
      return suivant;
    });

  const appliquer = async () => {
    if (appliquees === 0 || enregistrement.current) return;
    enregistrement.current = true;
    setLocalError(null);
    setPhase('applying');
    const amelioree = appliquerModificationsLettre(letter.content, changes, retenues);
    let enregistree: SavedCoverLetter;
    try {
      // `tBrut` : ce titre est ENREGISTRÉ en base, il ne doit pas porter les marques de direction
      // que `t` ajoute pour l'affichage en arabe.
      const title = tBrut('coverLetterImprove.copieTitre', { titre: letter.title });
      const id = await saveCoverLetter(amelioree, title, letter.output_language);
      enregistree = {
        id,
        title,
        created_at: new Date().toISOString(),
        content: amelioree,
        output_language: letter.output_language,
      };
    } catch (e) {
      if (!monte.current) return;
      setLocalError(e instanceof Error ? e.message : t('coverLetterImprove.saveFailed'));
      enregistrement.current = false;
      setPhase('revue');
      return;
    }
    // Hors du try : la lettre est enregistrée, une erreur de navigation ne doit ni afficher
    // « enregistrement impossible » ni permettre un second enregistrement.
    if (!monte.current) return;
    setNouvelle(enregistree);
    setFinished(true);
    setPhase('done');
    onDone?.(enregistree);
  };

  const shownError = localError ?? error;
  const enRevue = phase === 'revue' || phase === 'applying';

  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      <ScreenHeader variante="carte" onBack={onBack} testID="letterimprove-back" title={t('coverLetterImprove.title')} />

      <ScrollView contentContainerStyle={styles.content} keyboardShouldPersistTaps="handled">
        {phase === 'loading' ? (
          <View testID="letterimprove-loading" style={styles.center}>
            <GenerationProgress messageKey="improveLetter" testID="letterimprove-progress" />
          </View>
        ) : null}

        {phase === 'blocked' ? (
          <View style={styles.bloc}>
            <Text testID="letterimprove-blocked" style={styles.muted}>
              {shownError ?? t('coverLetterImprove.blocked')}
            </Text>
            {/* Un refus de quota ne se règle pas en réessayant : le Paywall s'en charge. */}
            {quotaFeature ? null : (
              <Button title={t('coverLetterImprove.retry')} variant="secondary" onPress={() => void lancer()} testID="letterimprove-retry" />
            )}
          </View>
        ) : null}

        {phase === 'done' && nouvelle ? (
          <View testID="letterimprove-fait" style={styles.bloc}>
            <Text accessibilityRole="header" style={styles.titre}>
              {t('coverLetterImprove.fait.titre')}
            </Text>
            <Text style={styles.texte}>{t('coverLetterImprove.fait.detail')}</Text>
            <Button title={t('coverLetterImprove.fait.ouvrir')} onPress={() => onDone?.(nouvelle)} testID="letterimprove-ouvrir" />
          </View>
        ) : null}

        {phase === 'vide' ? (
          <View testID="letterimprove-vide" style={styles.bloc}>
            <Text accessibilityRole="header" style={styles.titre}>
              {t('coverLetterImprove.vide.titre')}
            </Text>
            <Text style={styles.texte}>{t('coverLetterImprove.vide.detail')}</Text>
            <Button title={t('common.back')} variant="secondary" onPress={() => onBack?.()} testID="letterimprove-vide-retour" />
          </View>
        ) : null}

        {enRevue ? (
          <>
            <View style={styles.entete}>
              <Text testID="letterimprove-revue-titre" style={styles.titre} accessibilityRole="header">
                {t('coverLetterImprove.revue.titre', { count: total })}
              </Text>
              <Text style={styles.texte}>{t('coverLetterImprove.revue.aide')}</Text>
              <Text testID="letterimprove-longueur" style={styles.note}>
                {t('coverLetterImprove.revue.longueur', { avant: motsAvant, apres: motsApres })}
              </Text>
            </View>
            {changes.map((m) => (
              <CarteModificationLettre
                key={m.id}
                testID={`letterimprove-modif-${m.id}`}
                change={m}
                retenue={retenues.has(m.id)}
                onToggle={(retenue) => basculer(m.id, retenue)}
                disabled={phase === 'applying'}
              />
            ))}
          </>
        ) : null}
      </ScrollView>

      {enRevue ? (
        <PiedAction testID="letterimprove-pied">
          {/* Dans le pied, à côté du bouton : après plusieurs cartes, une erreur en bas de liste resterait hors de vue. */}
          {shownError ? (
            <Text testID="letterimprove-error" accessibilityLiveRegion="polite" style={styles.error}>
              {shownError}
            </Text>
          ) : null}
          <Text testID="letterimprove-retenues" style={styles.statut}>
            {t('coverLetterImprove.pied.retenues', { count: appliquees, total })}
          </Text>
          <GradientButton
            variante="cta"
            hauteur="lg"
            label={appliquees > 0 ? t('coverLetterImprove.pied.appliquer', { count: appliquees }) : t('coverLetterImprove.pied.aucune')}
            onPress={() => void appliquer()}
            disabled={appliquees === 0}
            loading={phase === 'applying'}
            testID="letterimprove-appliquer"
          />
        </PiedAction>
      ) : null}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.grey50 },
  content: { padding: screenPadding, gap: spacing.md },
  center: { alignItems: 'center', gap: spacing.sm, marginTop: spacing.xl },
  bloc: { gap: spacing.md, marginTop: spacing.lg },
  entete: { gap: spacing.xs },
  titre: { fontSize: typography.h2.size, fontWeight: typography.h2.weight, color: colors.night },
  texte: { fontSize: typography.body.size, lineHeight: typography.body.lineHeight, color: colors.grey500 },
  note: { fontSize: typography.small.size, lineHeight: typography.small.lineHeight, color: colors.grey500 },
  muted: { fontSize: typography.body.size, color: colors.grey500, textAlign: 'center' },
  error: { color: colors.error, fontSize: typography.small.size, textAlign: 'center' },
  statut: { fontSize: typography.small.size, fontWeight: '700', color: colors.grey500, textAlign: 'center' },
});
